import React, { useCallback, useEffect, Suspense } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { UIProvider } from "../../contexts/UIProvider";
import { ToastProvider, useToast } from "../../contexts/ToastContext";
import { UserProvider, useUser } from "../../contexts/UserContext";
import { DataProvider } from "../../contexts/DataContext";
import { AppProvider, useApp } from "../../contexts/AppContext";
import { TooltipProvider } from "../../contexts/TooltipContext";
import { PlanExperienceProvider } from "../../contexts/PlanExperienceContext";
import { NavigationIntentProvider } from "../../contexts/NavigationIntentContext";
import { NavigationProvider } from "../../contexts/NavigationContext";
import { ExperienceWizardProvider } from "../../contexts/ExperienceWizardContext";
import { DestinationWizardProvider } from "../../contexts/DestinationWizardContext";
import { UploadProgressProvider } from "../../contexts/UploadProgressContext";
import { lang } from "../../lang.constants";
import NavBar from "../../components/NavBar/NavBar";
import UploadProgressIndicator from "../../components/UploadProgressIndicator/UploadProgressIndicator";
import Loading from "../../components/LoadingWrapper/LoadingWrapper";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import SkipLink from "../../components/SkipLink/SkipLink";
import { handleOAuthCallback } from "../../utilities/oauth-service";
import { logger } from "../../utilities/logger";
import { getCollaboratorNotifications } from '../../utilities/notifications-api';
import { subscribeToEvent } from '../../utilities/event-bus';
import { useNavigate } from 'react-router-dom';
import CookieConsent from "../../components/CookieConsent/CookieConsent";
import ErrorBoundary from "../../components/ErrorBoundary/ErrorBoundary";
import MultiStepPlanModal from "../../components/MultiStepPlanModal/MultiStepPlanModal";
import LegalModalsHandler from "../../components/LegalModalsHandler/LegalModalsHandler";
import BienBotHashHandler from "../../components/BienBotHashHandler/BienBotHashHandler";
import BienBotTrigger from "../../components/BienBotTrigger/BienBotTrigger";
import { Helmet } from 'react-helmet-async';
import { Box, Container } from '@chakra-ui/react';
import ProfileSkeleton from "../Profile/components/ProfileSkeleton";
import { isSuperAdmin } from "../../utilities/permissions";
import {
  getObfuscatedJson,
  removeStorageKey,
  setObfuscatedJson
} from '../../utilities/secure-storage-lite';
import { STORAGE_KEYS } from '../../utilities/storage-keys';
import { isLegalHash, isBienBotHash } from '../../utilities/hash-navigation';
import { useNavigationCleanup } from '../../hooks/useNavigationCleanup';
import lazyWithRetry, { clearChunkReloadFlag } from '../../utilities/lazy-with-retry';

// Lazy load components for better performance (with automatic retry on stale chunks)
const AuthPage = lazyWithRetry(() => import("../AuthPage/AuthPage"));
const AppHome = lazyWithRetry(() => import("../AppHome/AppHome"));
const UpdateProfile = lazyWithRetry(() => import("../Profile/UpdateProfile"));
const SingleExperience = lazyWithRetry(() => import("../SingleExperience/SingleExperience"));
const SingleDestination = lazyWithRetry(() => import("../SingleDestination/SingleDestination"));
const Destinations = lazyWithRetry(() => import("../Destinations/Destinations"));
const Experiences = lazyWithRetry(() => import("../Experiences/Experiences"));
const ExperiencesByTag = lazyWithRetry(() => import("../ExperiencesByTag/ExperiencesByTag"));
const NewExperience = lazyWithRetry(() => import("../../components/NewExperience/NewExperience"));
const UpdateExperience = lazyWithRetry(() => import("../../components/UpdateExperience/UpdateExperience"));
const NewDestination = lazyWithRetry(() => import("../../components/NewDestination/NewDestination"));
const UpdateDestination = lazyWithRetry(() => import("../../components/UpdateDestination/UpdateDestination"));
const Profile = lazyWithRetry(() => import("../Profile/Profile"));
const AllUsers = lazyWithRetry(() => import("../AllUsers/AllUsers"));
const AIAdmin = lazyWithRetry(() => import("../AIAdmin/AIAdmin"));
const InviteTracking = lazyWithRetry(() => import("../InviteTracking/InviteTracking"));
const ResetPassword = lazyWithRetry(() => import("../ResetPassword/ResetPassword"));
const ConfirmEmail = lazyWithRetry(() => import("../ConfirmEmail/ConfirmEmail"));
const Dashboard = lazyWithRetry(() => import("../Dashboard/Dashboard"));
const Countries = lazyWithRetry(() => import("../Countries/Countries"));

logger.info('App.jsx loaded');

/**
 * Main application component wrapper
 * Provides all context providers in the correct order
 */
export default function App() {
  logger.debug('App component function called');
  return (
    <HelmetProvider>
      <Helmet>
        {/* Basic meta tags */}
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#9333ea" />

        {/* Open Graph defaults */}
        <meta property="og:site_name" content="Biensperience" />
        <meta property="og:type" content="website" />

        {/* Schema.org defaults */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            'name': 'Biensperience',
            'alternateName': 'Biensperience Travel Planning Platform',
            'description': 'Visual travel experience platform for planning and sharing adventures. Discover curated destinations, create your travel bucket list, and organize your adventures with fellow travelers.',
            'url': window.location.origin,
            'publisher': {
              '@type': 'Organization',
              'name': 'Biensperience',
              'description': 'A platform connecting travelers and adventurers worldwide',
              'url': window.location.origin,
              'logo': `${window.location.origin}/logo.png`,
              'sameAs': [
                'https://twitter.com/biensperience'
              ]
            },
            'potentialAction': {
              '@type': 'SearchAction',
              'target': `${window.location.origin}/experiences?search={search_term_string}`,
              'query-input': 'required name=search_term_string'
            },
            'about': {
              '@type': 'Thing',
              'name': 'Travel Planning',
              'description': 'Planning and organizing travel experiences and adventures'
            },
            'audience': {
              '@type': 'Audience',
              'audienceType': 'Travelers and adventurers'
            }
          })}
        </script>
      </Helmet>
      <UIProvider>
        <ToastProvider>
          <UserProvider>
            <AppProvider>
              <DataProvider>
                <TooltipProvider>
                  <PlanExperienceProvider>
                    <NavigationIntentProvider>
                      <NavigationProvider>
                        <ExperienceWizardProvider>
                        <DestinationWizardProvider>
                          <UploadProgressProvider>
                            <AppContent />
                          </UploadProgressProvider>
                        </DestinationWizardProvider>
                        </ExperienceWizardProvider>
                      </NavigationProvider>
                    </NavigationIntentProvider>
                  </PlanExperienceProvider>
                </TooltipProvider>
              </DataProvider>
            </AppProvider>
          </UserProvider>
        </ToastProvider>
      </UIProvider>
    </HelmetProvider>
  );
}

/**
 * App content component that uses contexts
 * Separated from App to allow hooks usage
 */
function AppContent() {
  logger.debug('AppContent component function called');

  // Clear the stale-chunk reload guard on successful mount so future
  // deploys can still trigger a fresh reload if needed.
  useEffect(() => { clearChunkReloadFlag(); }, []);

  logger.debug('About to call useUser');
  const { user, updateUser, isAuthenticated } = useUser();
  logger.debug('useUser completed', { isAuthenticated });

  logger.debug('About to call useApp');
  useApp(); // Initializes app context (scroll tracking, etc.)
  logger.debug('useApp completed');

  // Cancel stale queued requests when navigating between views
  useNavigationCleanup();

  logger.debug('About to call useToast');
  const { success, error: showError, addToast } = useToast();
  logger.debug('useToast completed');

  const navigate = useNavigate();
  const location = useLocation();

  // OAuth processing state
  const [isProcessingOAuth, setIsProcessingOAuth] = React.useState(false);
  const oauthProcessedRef = React.useRef(false);

  // Notification state for BienBotTrigger
  const [notifications, setNotifications] = React.useState([]);
  const [unseenNotificationIds, setUnseenNotificationIds] = React.useState([]);

  const PENDING_HASH_STORAGE_KEY = STORAGE_KEYS.pendingHash;

  const applyHashToUrl = useCallback((hash, { mode } = {}) => {
    const normalized = (hash || '').startsWith('#') ? hash : `#${hash || ''}`;
    if (!normalized || normalized === '#') return;

    const nextUrl = `${window.location.pathname}${window.location.search || ''}${normalized}`;

    if (mode === 'replace') {
      window.history.replaceState(window.history.state, '', nextUrl);
    } else {
      // Default to push so the hash is represented in browser history.
      window.history.pushState(window.history.state, '', nextUrl);
    }

    // React Router listens to popstate for location updates; pushState/replaceState
    // don't emit it. Dispatching keeps router location in sync with the address bar.
    try {
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (e) {
      // ignore
    }
  }, []);

  const parsePendingHash = (rawValue) => {
    if (!rawValue) return null;

    // Back-compat: older values were stored as a raw string hash
    if (rawValue.startsWith('#')) {
      return { hash: rawValue, originPath: null, targetPath: null };
    }

    try {
      const parsed = JSON.parse(rawValue);
      if (parsed && typeof parsed === 'object') {
        return {
          hash: parsed.hash || null,
          originPath: parsed.originPath || null,
          targetPath: parsed.targetPath || null
        };
      }
    } catch (e) {
      // ignore
    }

    return { hash: rawValue, originPath: null, targetPath: null };
  };

  // Store intended route for post-login redirect when user is not authenticated
  // This enables deep linking - users can access protected routes after login
  useEffect(() => {
    if (!isAuthenticated) {
      const currentPath = location.pathname + location.search + location.hash;
      // Don't store auth-related paths or the home page as intended routes
      const excludedPaths = ['/', '/signup', '/reset-password', '/confirm-email'];
      const shouldStore = !excludedPaths.some(path =>
        currentPath === path || currentPath.startsWith('/reset-password/') || currentPath.startsWith('/confirm-email/')
      );

      if (shouldStore && currentPath !== '/') {
        setObfuscatedJson(sessionStorage, 'bien:intendedRoute', currentPath);
        logger.info('[Login Redirect] Stored intended route:', currentPath);
      }
    }
  }, [isAuthenticated, location]);

  // Preserve hash fragments - React Router strips them during navigation
  // This effect captures and restores hashes for deep linking
  useEffect(() => {
    // Store hash on initial load (handles direct URL paste)
    // Skip legal/policy hashes — they are transient modal triggers handled by
    // LegalModalsHandler and should not survive login redirects.
    const initialHash = window.location.hash;
    if (initialHash && !isLegalHash(initialHash) && !isBienBotHash(initialHash)) {
      logger.info('[Hash Preservation] Initial hash detected:', initialHash);
      // Store in localStorage to survive React Router navigation that may strip hashes
      // Save origin pathname so we only restore on the same view when possible
      try {
        const payload = JSON.stringify({ hash: initialHash, originPath: window.location.pathname });
        localStorage.setItem(PENDING_HASH_STORAGE_KEY, payload);
      } catch (e) {
        localStorage.setItem(PENDING_HASH_STORAGE_KEY, initialHash);
      }
    }

    // Global click handler to capture hashes from Link navigation
    const handleClick = (e) => {
      const link = e.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (href && href.includes('#')) {
        const hashIndex = href.indexOf('#');
        const hash = href.substring(hashIndex);
        // Skip legal/policy and bienbot hashes — handled by their own modal/hash handlers
        if (isLegalHash(hash) || isBienBotHash(hash)) return;
        const targetPath = href.substring(0, hashIndex) || null;
        logger.info('[Hash Preservation] Captured hash from link:', hash);
        try {
          const payload = JSON.stringify({ hash, originPath: window.location.pathname, targetPath });
          localStorage.setItem(PENDING_HASH_STORAGE_KEY, payload);
        } catch (e) {
          localStorage.setItem(PENDING_HASH_STORAGE_KEY, hash);
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  // Restore pending hashes after route changes.
  // React Router navigation often drops the hash from the address bar; deep-link consumers
  // (like SingleExperience) may still act on the intent, but we want the URL to reflect it.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const raw = localStorage.getItem(PENDING_HASH_STORAGE_KEY);
    const stored = parsePendingHash(raw);
    const storedHash = stored?.hash || '';
    if (!storedHash) {
      if (raw) localStorage.removeItem(PENDING_HASH_STORAGE_KEY);
      return;
    }

    // If something already set a hash (e.g., destination view), don't override it.
    if (window.location.hash && window.location.hash !== storedHash) {
      localStorage.removeItem(PENDING_HASH_STORAGE_KEY);
      return;
    }

    // Avoid restoring on the auth page shell
    if (!isAuthenticated) return;

    const currentPath = `${location.pathname}${location.search || ''}`;
    const normalizedStoredHash = storedHash.startsWith('#') ? storedHash : `#${storedHash}`;

    const targetPathMatches = !!(stored?.targetPath && (stored.targetPath === currentPath || stored.targetPath === location.pathname));
    const originPathMatches = !!(stored?.originPath && stored.originPath === window.location.pathname);
    const isPlanHash = normalizedStoredHash.startsWith('#plan-');
    const isExperienceRoute = /\/experiences\//.test(location.pathname || '');

    logger.info('[Hash Preservation] Checking pending hash for restore:', {
      normalizedStoredHash,
      currentPath,
      targetPath: stored?.targetPath || null,
      originPath: stored?.originPath || null,
      targetPathMatches,
      originPathMatches,
      isPlanHash,
      isExperienceRoute
    });

    // Restore rules:
    // - If we know the intended targetPath and it matches, restore.
    // - Legacy behavior: if originPath matches, restore.
    // - Plan deep links: restore on any experience route.
    const shouldRestore = targetPathMatches || originPathMatches || (isPlanHash && isExperienceRoute);
    if (!shouldRestore) return;

    // Restore after a brief delay to ensure the destination view has mounted.
    const t = setTimeout(() => {
      try {
        if (!window.location.hash) {
          // If this was a direct-load paste (no known target), avoid polluting history.
          // Otherwise, push so back/forward reflects hash changes.
          const restoreMode = originPathMatches && !stored?.targetPath ? 'replace' : 'push';
          applyHashToUrl(normalizedStoredHash, { mode: restoreMode });
          logger.info('[Hash Preservation] Restored pending hash after navigation');
        }
      } finally {
        localStorage.removeItem(PENDING_HASH_STORAGE_KEY);
      }
    }, 50);

    return () => clearTimeout(t);
  }, [location.pathname, location.search, isAuthenticated, applyHashToUrl]);

  // NOTE: CSS loading detection (waitForCSS / initializeCSSEnvironment) removed
  // during Chakra migration (P4.1). Chakra uses CSS-in-JS, so stylesheet
  // timing detection is no longer needed for Chakra components. Remaining SCSS
  // files still load via Vite but don't require explicit detection.

  // Mark notification IDs as seen in localStorage and state
  const markNotificationsSeen = useCallback((ids) => {
    if (!user?._id || !ids?.length) return;
    const seenKey = `bien:seenActivities:${user._id}`;
    const cur = getObfuscatedJson(localStorage, seenKey, []);
    const merged = Array.from(new Set([...(Array.isArray(cur) ? cur : []), ...ids]));
    setObfuscatedJson(localStorage, seenKey, merged);
    setUnseenNotificationIds(prev => prev.filter(id => !ids.includes(id)));
  }, [user?._id]);

  // Effect: fetch collaborator notifications on login/hard refresh
  useEffect(() => {
    let mounted = true;

    const fetchNotifications = async () => {
      try {
        if (!isAuthenticated || !user?._id) return;
        const userId = user._id;

        const raw = await getCollaboratorNotifications(userId, { limit: 10 });
        const activities = Array.isArray(raw) ? raw : (raw?.data || []);

        const seenKey = `bien:seenActivities:${userId}`;
        const legacySeenKey = `seen_activities_${userId}`;

        let seen = getObfuscatedJson(localStorage, seenKey, null);
        if (!Array.isArray(seen)) {
          // Migrate legacy plaintext list if present
          try {
            const legacyRaw = localStorage.getItem(legacySeenKey);
            if (legacyRaw) {
              const parsed = JSON.parse(legacyRaw);
              if (Array.isArray(parsed)) {
                seen = parsed;
                setObfuscatedJson(localStorage, seenKey, parsed);
              }
              try { localStorage.removeItem(legacySeenKey); } catch (e) {}
            }
          } catch (e) {
            seen = [];
          }
        }
        if (!Array.isArray(seen)) seen = [];

        // Track all activities and unseen IDs for BienBotTrigger
        if (mounted) {
          setNotifications(activities);
          const unseen = activities
            .filter(act => act?._id && !seen.includes(act._id))
            .map(act => act._id);
          setUnseenNotificationIds(unseen);
        }

        // Show toasts for unseen notifications
        const newIds = [];

        activities.forEach(act => {
          if (!mounted) return;
          if (!act || !act._id) return;
          if (seen.includes(act._id)) return;

          const message = act.reason || `${act.actor?.name || 'Someone'} added you as a collaborator to ${act.resource?.name || 'an experience'}`;
          addToast({
            message,
            header: 'New collaborator',
            type: 'primary',
            duration: 10000,
            actions: [
              {
                label: 'View',
                onClick: () => {
                  if (act.resource && (act.resource.id || act.resource._id)) {
                    const id = act.resource.id || act.resource._id;
                    navigate(`/experiences/${id}`);
                  }
                  markNotificationsSeen([act._id]);
                },
                variant: 'primary'
              }
            ]
          });

          newIds.push(act._id);
        });

        if (mounted && newIds.length > 0) {
          const cur = getObfuscatedJson(localStorage, seenKey, []);
          const merged = Array.from(new Set([...(Array.isArray(cur) ? cur : []), ...newIds]));
          setObfuscatedJson(localStorage, seenKey, merged);
        }
      } catch (err) {
        logger.error('Failed to fetch collaborator notifications', { error: err?.message || err });
      }
    };

    fetchNotifications();

    return () => { mounted = false; };
  }, [isAuthenticated, user?._id, addToast, navigate, markNotificationsSeen]);

  // Effect: real-time badge update when a notification:received WS event arrives
  useEffect(() => {
    if (!isAuthenticated || !user?._id) return;
    const userId = user._id;
    const seenKey = `bien:seenActivities:${userId}`;

    const unsubscribe = subscribeToEvent('notification:received', (data) => {
      const activity = data?.notification;
      if (!activity?._id) return;

      setNotifications(prev => {
        if (prev.some(n => n._id === activity._id)) return prev;
        return [activity, ...prev];
      });

      const seen = getObfuscatedJson(localStorage, seenKey, []);
      if (!seen.includes(activity._id)) {
        setUnseenNotificationIds(prev =>
          prev.includes(activity._id) ? prev : [activity._id, ...prev]
        );
      }
    });

    return unsubscribe;
  }, [isAuthenticated, user?._id]);

  // Effect: handle OAuth callback once on mount
  useEffect(() => {
    // Prevent duplicate OAuth processing in React StrictMode
    if (oauthProcessedRef.current) {
      return;
    }
    oauthProcessedRef.current = true;

    const processOAuth = async () => {
      // Check if we have OAuth params to process
      const urlParams = new URLSearchParams(window.location.search);
      const hasOAuthParam = urlParams.has('oauth');

      if (!hasOAuthParam) {
        return; // No OAuth processing needed
      }

      try {
        setIsProcessingOAuth(true);
        const result = await handleOAuthCallback();
        if (result) {
          const { user: oauthUser, provider } = result;
          updateUser(oauthUser);
          
          // Map provider names to display names
          const providerDisplayNames = {
            facebook: 'Facebook',
            google: 'Google',
            twitter: 'X'
          };
          const displayProvider = providerDisplayNames[provider] || provider.charAt(0).toUpperCase() + provider.slice(1);
          
          const message = lang.current.notification?.auth?.oauthSuccess?.replace('{provider}', displayProvider) || `Welcome back! You're signed in with ${displayProvider}.`;
          success(message);

          // Redirect to intended route after OAuth login (deep linking support)
          const intendedRoute = getObfuscatedJson(sessionStorage, 'bien:intendedRoute', null);
          if (intendedRoute) {
            removeStorageKey(sessionStorage, 'bien:intendedRoute');
            logger.info('[Login Redirect] Redirecting to intended route after OAuth:', intendedRoute);
            navigate(intendedRoute);
          }
        }
      } catch (err) {
        showError(err.message || 'Authentication failed');
      } finally {
        // Always remove oauth query param after processing (success or failure)
        const url = new URL(window.location);
        url.searchParams.delete('oauth');
        // Strip Facebook's #_=_ hash fragment that gets appended after OAuth
        const hash = url.hash === '#_=_' ? '' : url.hash;
        window.history.replaceState({}, document.title, url.pathname + hash);
        
        setIsProcessingOAuth(false);
      }
    };

    processOAuth();
    // Intentionally leave deps empty to run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const RouteSuspenseFallback = useCallback(() => {
      try {
        const pathname = (location?.pathname || '').toLowerCase();
        const segments = pathname.split('/').filter(Boolean);

        const isProfileViewRoute =
          segments[0] === 'profile' &&
          (segments.length === 1 || (segments.length === 2 && segments[1] && segments[1] !== 'update'));

        if (!isProfileViewRoute) {
          return <Loading variant="centered" size="lg" message={lang.current.loading.page} />;
        }

        const profileIdFromPath = segments.length === 2 && segments[1]?.length === 24 ? segments[1] : null;
        const isOwner = !profileIdFromPath || profileIdFromPath === user?._id || isSuperAdmin(user);

        const rawHash = (window.location.hash || '').replace('#', '');
        const activeTab = ['activity', 'follows', 'experiences', 'created', 'destinations'].includes(rawHash)
          ? rawHash
          : (rawHash === 'followers' || rawHash === 'following')
          ? 'follows'
          : 'activity';

        return (
          <Box bg="bg" minH="100vh" py="8">
            <Container>
              <ProfileSkeleton isOwner={isOwner} activeTab={activeTab} />
            </Container>
          </Box>
        );
      } catch (e) {
        return <Loading variant="centered" size="lg" message={lang.current.loading.page} />;
      }
    }, [location?.pathname, user]);

    return (
      <>
        {/* Skip link for keyboard navigation - WCAG 2.1 SC 2.4.1 Bypass Blocks */}
        <SkipLink />
        <Box
          pt="78px"
          pb="8"
          minH="100dvh"
          bg="bg"
        >
          <CookieConsent />
          {/* Legal modals accessible via hash links (#terms, #privacy) */}
          <LegalModalsHandler />
          {/* BienBot session deep links via hash (#bienbot-session-{id}) */}
          {isAuthenticated && <BienBotHashHandler />}
          {/* Multi-step Plan Experience Modal - globally accessible */}
          {isAuthenticated && <MultiStepPlanModal />}
          {isAuthenticated && (
            <BienBotTrigger
              notifications={notifications}
              unseenNotificationIds={unseenNotificationIds}
              onMarkNotificationsSeen={markNotificationsSeen}
            />
          )}
          {isProcessingOAuth ? (
            <Container as="main" id="main-content" role="main" aria-label={lang.current.aria.mainContent}>
              <Loading variant="centered" size="lg" message="Completing sign in..." />
            </Container>
          ) : isAuthenticated ? (
            <>
              <NavBar />
              <UploadProgressIndicator />
              <Container as="main" id="main-content" role="main" aria-label={lang.current.aria.mainContent}>
                <ErrorBoundary
                  title={lang.current.modal.pageError}
                  message={lang.current.modal.errorLoadingPage}
                  showHomeButton={true}
                >
                  <ScrollToTop />
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <Routes>
                      <Route path="/" element={<AppHome />} />
                      <Route path="/experiences/new" element={
                        <ErrorBoundary title={lang.current.modal.formError} message={lang.current.modal.errorLoadingForm}>
                          <NewExperience />
                        </ErrorBoundary>
                      } />
                      <Route path="/destinations/new" element={
                        <ErrorBoundary title={lang.current.modal.formError} message={lang.current.modal.errorLoadingForm}>
                          <NewDestination />
                        </ErrorBoundary>
                      } />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/profile/:profileId" element={<Profile />} />
                      <Route path="/profile/update" element={<UpdateProfile />} />
                      <Route path="/profile/:userId/update" element={<UpdateProfile />} />
                      <Route path="/invites" element={<InviteTracking />} />
                      <Route path="/admin/users" element={<AllUsers />} />
                      <Route path="/admin/ai" element={<AIAdmin />} />
                      <Route path="/experiences" element={<Experiences />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/destinations" element={<Destinations />} />
                      <Route path="/countries/:countryName" element={<Countries />} />
                      <Route path="/experience-types/:tagName" element={<ExperiencesByTag />} />
                      <Route path="/experiences/:experienceId" element={
                        <ErrorBoundary title={lang.current.modal.experienceError} message={lang.current.modal.errorLoadingExperience}>
                          <SingleExperience />
                        </ErrorBoundary>
                      } />
                      <Route path="/confirm-email/:token" element={<ConfirmEmail />} />
                      <Route path="/experiences/:experienceId/update" element={
                        <ErrorBoundary title={lang.current.modal.formError} message={lang.current.modal.errorLoadingForm}>
                          <UpdateExperience />
                        </ErrorBoundary>
                      } />
                      <Route path="/destinations/:destinationId" element={
                        <ErrorBoundary title={lang.current.modal.destinationError} message={lang.current.modal.errorLoadingDestination}>
                          <SingleDestination />
                        </ErrorBoundary>
                      } />
                      <Route path="/destinations/:destinationId/update" element={
                        <ErrorBoundary title={lang.current.modal.formError} message={lang.current.modal.errorLoadingForm}>
                          <UpdateDestination />
                        </ErrorBoundary>
                      } />
                      <Route path="/logout" element={<Navigate to="/" />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </Container>
            </>
          ) : (
            <Container as="main" id="main-content" role="main" aria-label={lang.current.aria.authentication}>
              <Suspense fallback={<Loading variant="centered" size="lg" /> }>
                <Routes>
                  <Route path="/signup" element={<AuthPage />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/confirm-email/:token" element={<ConfirmEmail />} />
                  <Route path="*" element={<AuthPage />} />
                </Routes>
              </Suspense>
            </Container>
          )}
        </Box>
      </>
    );
}
